// Audio format conversion utilities for better Azure Speech compatibility

export interface AudioConversionResult {
  convertedBlob: Blob;
  originalFormat: string;
  convertedFormat: string;
  originalSize: number;
  convertedSize: number;
}

/**
 * Convert WebM/Opus audio to WAV format for better Azure Speech compatibility
 */
export async function convertToWav(audioBlob: Blob): Promise<AudioConversionResult> {
  console.log('Starting audio conversion from', audioBlob.type, 'to WAV');
  
  try {
    // Decode the audio blob to raw audio data
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // Convert to WAV format
    const wavBlob = audioBufferToWav(audioBuffer);
    
    console.log('Audio conversion completed:', {
      originalFormat: audioBlob.type,
      convertedFormat: 'audio/wav',
      originalSize: audioBlob.size,
      convertedSize: wavBlob.size,
      compressionRatio: (wavBlob.size / audioBlob.size).toFixed(2)
    });
    
    return {
      convertedBlob: wavBlob,
      originalFormat: audioBlob.type,
      convertedFormat: 'audio/wav',
      originalSize: audioBlob.size,
      convertedSize: wavBlob.size
    };
  } catch (error) {
    console.error('Audio conversion failed:', error);
    throw new Error('Failed to convert audio to WAV format: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}

/**
 * Convert AudioBuffer to WAV Blob
 */
function audioBufferToWav(audioBuffer: AudioBuffer): Blob {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const format = 1; // PCM format
  const bitDepth = 16;
  
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numberOfChannels * bytesPerSample;
  
  // Get audio data
  const length = audioBuffer.length;
  const arrayBuffer = new ArrayBuffer(44 + length * numberOfChannels * bytesPerSample);
  const view = new DataView(arrayBuffer);
  
  // Write WAV header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + length * numberOfChannels * bytesPerSample, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, format, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, length * numberOfChannels * bytesPerSample, true);
  
  // Write audio data
  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = audioBuffer.getChannelData(channel)[i];
      const intSample = Math.max(-32768, Math.min(32767, sample * 32767));
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

/**
 * Write string to DataView
 */
function writeString(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

/**
 * Check if audio format is compatible with Azure Speech
 */
export function isAzureCompatibleFormat(mimeType: string): boolean {
  const compatibleFormats = [
    'audio/wav',
    'audio/mp3',
    'audio/mpeg',
    'audio/flac',
    'audio/mp4',
    'audio/aac'
  ];
  
  return compatibleFormats.some(format => mimeType.includes(format));
}

/**
 * Get recommended settings for recording based on Azure Speech requirements
 */
export function getAzureOptimizedRecordingSettings() {
  return {
    audio: {
      sampleRate: 16000, // Azure Speech optimal sample rate
      channelCount: 1,   // Mono audio
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    }
  };
}